import { Box } from '@modusign/design-system';
import { useCompanyNameField } from '@modusign/react-signing';
import { cx } from 'emotion';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle, useUpdateEffect } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import { getTextBaseFieldCorrectedSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldCorrectedSize';
import { getTextBaseFieldMinimumSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldMinimumSize';
import { ConfigurableAutofillFieldGroupRendererProps } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/AutofillFieldGroupRenderer';
import {
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import CompanyNameFieldPlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/CompanyNameFieldPlaceholder';
import TextBaseFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/TextBaseFieldContextMenu';
import { useTransformTextStyle } from '@app/features/apply-react-signing/libs/hooks/useTransFormTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './ConfigurableCompanyNameField.scss';

export function ConfigurableCompanyNameFieldRenderer({
  restrictModification,
  readOnly,
  fieldId,
  scale,
  viewport,
  isDataLabelEditable,
  isDataLabelDuplicated,
  onFieldRemove,
}: ConfigurableAutofillFieldGroupRendererProps) {
  const [isResizing, setResizing] = useState(false);
  const [showContextMenu, toggleContextMenu] = useToggle(true);
  const [showGuideline, setShowGuideline] = useState(false);
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setAlign,
    setFontSize,
    setFontStyle,
    setRequired,
  } = useCompanyNameField(fieldId);

  const fontSize = field?.config.fontStyle.size ?? 60;
  const fontPixelSize: number = PDFFontNormalizer.toPixelFontSize({ fontSize }, scale);

  const absoluteSize = toAbsoluteSize(field?.size ?? { width: 0, height: 0 }, viewport);
  const absolutePoint = toAbsolutePoint(field?.position ?? { x: 0, y: 0 }, viewport);

  // 'g', 'j', 'q' 등과 같이 폰트 높이 아래로 튀어 나오는 영역을 보정하기 위한 값
  const minSize = getTextBaseFieldMinimumSize(fontSize, scale);

  const { transformTextStyle } = useTransformTextStyle({
    fontFamily: field?.config.fontStyle.type ?? 'NOTO_SANS',
    textAlign: field?.config.textAlign ?? 'LEFT',
    fontSize: field?.config.fontStyle.size ?? 12,
    size: absoluteSize,
    scale,
  });

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [field?.dataLabel, isDataLabelDuplicated]);

  const correctSize = () => {
    const relativeSize = toRelativeSize(
      getTextBaseFieldCorrectedSize({
        size: absoluteSize,
        scale,
        fontSize: field?.config.fontStyle.size ?? 60,
      }),
      viewport
    );
    setSize(relativeSize.width, relativeSize.height);
  };

  // 폰트크기가 변경되었을 때, 최소 크기와 글줄 길이에 맞게 보정합니다.
  useUpdateEffect(() => {
    correctSize();
  }, [fontPixelSize]);

  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const handlePointChangeStart = () => {
    setShowGuideline(true);
    toggleContextMenu(true);
  };

  const handlePointChangeStop = () => {
    setShowGuideline(false);
  };

  const handlePointChange = (point: AbsolutePoint) => {
    const relativePoint = clampFieldPoint(toRelativePoint(point, viewport));
    setPosition(relativePoint.x, relativePoint.y);
    setShowGuideline(true);
  };

  const handleChangeSizeStart = () => {
    setResizing(true);
    setShowGuideline(true);
  };

  const handleChangeSize = (size: AbsoluteSize) => {
    const relativeSize = toRelativeSize(size, viewport);
    setShowGuideline(true);
    setSize(relativeSize.width, relativeSize.height);
  };

  const handleChangeSizeStop = () => {
    setResizing(false);
    setShowGuideline(false);
    correctSize();
  };

  const handleRemoveField = () => {
    onFieldRemove(fieldId);
  };

  const participantOrder = field.participantReference.index + 1;

  const configurableCompanyNameFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableCompanyNameField',
    participantOrder
  );

  const handleChangeFontSize = (fontSize: number) => {
    setFontSize(fontSize);
  };

  return (
    <FieldBase
      className={cx({
        ConfigurableCompanyNameField: true,
        'ConfigurableCompanyNameField--resizing': isResizing,
        [configurableCompanyNameFieldClassByOrder]: true,
        'ConfigurableCompanyNameField__disable-resize': readOnly || restrictModification,
      })}
      data-testid="company-name-field"
      dragCancelSelectors={['.ConfigurableCompanyNameField__context-menu']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1,
          bottom: 1,
        },
        viewport,
        showGuideLine: showGuideline,
      }}
      maxConstraints={[viewport.width - absolutePoint.x, viewport.height - absolutePoint.y]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      restrictModification={restrictModification}
      size={absoluteSize}
      snapSize={createAbsoluteSize({
        width: 1,
        height: fontPixelSize,
      })}
      onPointChange={handlePointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeStop}
      onSizeChange={handleChangeSize}
      onSizeChangeStart={handleChangeSizeStart}
      onSizeChangeStop={handleChangeSizeStop}
    >
      <Box height="100%" width="100%">
        <OutsideClickHandler
          onOutsideClick={() => {
            if (field.dataLabel && !isDataLabelDuplicated) {
              toggleContextMenu(false);
            }
          }}
        >
          {showContextMenu && !readOnly && !restrictModification && (
            <Box className="ConfigurableCompanyNameField__context-menu">
              <TextBaseFieldContextMenu
                dataLabel={field.dataLabel}
                dataLabelErrorMessage={dataLabelErrorMessage}
                fontFamily={field.config.fontStyle.type}
                fontSize={field.config.fontStyle.size}
                isDataLabelEditable={isDataLabelEditable}
                isRequired={field.config.required}
                participantName={getSignerDisplayName(field.participantReference)}
                participantOrder={participantOrder}
                textAlign={field.config.textAlign}
                onDataLabelChange={(dataLabel) => setDataLabel(dataLabel)}
                onFontFamilyChange={(fontFamily) => setFontStyle(fontFamily)}
                onFontSizeChange={handleChangeFontSize}
                onRequiredToggle={(_, required) => setRequired(required)}
                onTextAlignChange={(align) => setAlign(align)}
              />
            </Box>
          )}
          <Box height="100%" position="absolute" width="100%">
            <CompanyNameFieldPlaceholder
              fontPixelSize={fontPixelSize}
              participantOrder={participantOrder}
              star={field.config.required}
              style={transformTextStyle}
            />
            {!readOnly && !restrictModification && (
              <Box className="ConfigurableCompanyNameField__x-button">
                <RoundedXButton onClick={handleRemoveField} />
              </Box>
            )}
          </Box>
        </OutsideClickHandler>
      </Box>
    </FieldBase>
  );
}
